/**
 * The **pilot** spawn adapter (direct on-foot deploy), plus the frozen `pilotBlock` the mech
 * adapter embeds for its own cockpit pilot.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { AffordanceLimited, AffordanceSource, CombatantBase, FrozenPilotBlock, PilotData, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";
import { affordanceKey, BIOLOGICAL_STATUS } from "@lancer-vtt/domain";
import { ok } from "@lancer-vtt/shared";
import { coreActionIdsFor } from "../coreActionCatalog";
import { compute, type ComputedPilot } from "../../compute";
import { freezeWeaponProfile, parseRange, sidearmFightAllowance, type RawWeapon } from "../weaponProfile";
import { buildAffordances, rawItemName, type RawActionBearer } from "./actionFreeze";
import { freezePilotGearEffectIds } from "./effectFreeze";
import type { SpawnResult } from "./types";

/**
 * The subset of a `pilot_gear` compendium entry the on-foot freeze reads. Loosely
 * typed like {@link RawWeapon} (the compendium's `.passthrough()` pattern, whose
 * `type?: unknown` field this reads): only a `"Weapon"`-typed gear entry (Archaic
 * Melee, Light/Medium/Heavy A/C, the Signature weapons) carries a damage profile;
 * armor/utility gear entries carry none and are skipped by {@link freezePilotGearWeapons}.
 * **Note the vocabulary clash**: this SAME `type` field is a gear-kind classifier
 * here (`"Weapon"`/`"Armor"`/`"Gear"`) but a weapon-CLASS field on a mech weapon
 * (`"Rifle"`/`"CQB"`/…) — `freezeWeaponProfile`'s `normalizeWeaponType` validates
 * against the closed weapon-class set, so a pilot-gear entry's `"Weapon"` value
 * never leaks onto the frozen `WeaponProfile.type`.
 */
export type RawPilotGear = RawWeapon;

/**
 * Freeze a {@link WeaponProfile} for every **weapon-typed** entry in the pilot's
 * `gear` list. Pilot-gear weapons are the ONLY pilot weapons — there is no
 * mech-scale fallback. Mirrors {@link freezeWeapons}: resolves each id out of
 * the compendium once, parses its damage/tags/range into a frozen profile, and
 * skips an unresolved id or a non-weapon gear entry (armor/utility) — a gearless
 * or weaponless pilot deploys with `weapons: []` and Fights with nothing
 * (honest-degrade, like a weaponless mech).
 *
 * **Instance ids** namespace as `pilotgear:${i}` — `i` is the pilot's `gear[]`
 * array index, distinct from a mech's `${mountIndex}:${slotIndex}`/`integrated:${i}`
 * namespaces (the disjoint-namespace discipline every instance key follows).
 *
 * A Set Damage Type weapon's picked damage type (`PilotData.gearDamageTypes`, the Signatures)
 * replaces its printed Variable, so the attack asks for none; unpicked, it stays Variable.
 */
export function freezePilotGearWeapons(pilot: PilotData, compendium: Compendium): WeaponProfile[] {
  const profiles: WeaponProfile[] = [];
  pilot.gear.forEach((id, i) => {
    const gear = compendium.getById("pilot_gear", id) as RawPilotGear | undefined;
    if (!gear) return; // unresolved id — honest degrade, like every other freeze
    if (typeof gear.type !== "string" || gear.type.toLowerCase() !== "weapon") return; // armor/utility gear carries no damage
    const profile = freezeWeaponProfile(gear, id, `pilotgear:${i}`);
    const picked = pilot.gearDamageTypes?.[id];
    profiles.push(picked !== undefined ? { ...profile, damage: { ...profile.damage, type: picked } } : profile);
  });
  return profiles;
}

/**
 * Freeze the pilot's **gear-granted affordance sources** —
 * mirrors {@link freezeActionIds} over `pilot.gear` instead of a mech loadout: a
 * gear entry carrying an `actions[]` array (Use Frag Grenade, Use Patch, Activate
 * Stealth Hardsuit, …) emits the composite overlay id `${gearId}:${index}` per
 * action. A gear entry ALSO mints one composite id per `deployables[]` entry,
 * OFFSET past its own `actions.length` (the unified affordance space — `actions[]`
 * first, then `deployables[]` — mirroring `freezeActionCaps`'s
 * `collectSystem` mech-side sibling id-mint-for-id-mint). `pg_thermite_charge` is the worked case:
 * zero `actions[]`, one `deployables[0]` ⇒ mints exactly `pg_thermite_charge:0`.
 * Same honest-degrade discipline: an unresolved id, or one with NEITHER
 * `actions[]` NOR `deployables[]`, contributes nothing.
 *
 * `ranges` is each minted action's RAW reach (`CombatantBase.actionRanges`, the mech walk's
 * `freezeActionIds` twin): Frag Grenades' "within Range 5" freezes here, so `checkActivateSiteRange`
 * gates the throw. Sparse: an action with no `range` (Patch, Stims) lands no key.
 */
function freezePilotGearActionIds(
  pilot: PilotData,
  compendium: Compendium,
): { ids: string[]; sources: AffordanceSource[]; names: Record<string, string>; ranges: Record<string, WeaponRange[]> } {
  const ids: string[] = [];
  // The STRUCTURED half of the same mint, positionally parallel to `ids` (`ids[n]` is always
  // `affordanceKey(sources[n])`) — the pilot-gear mirror of `freezeActionIds`' own `sources`.
  const sources: AffordanceSource[] = [];
  const names: Record<string, string> = {};
  const ranges: Record<string, WeaponRange[]> = {};
  const rawRange = (entry: unknown): unknown => (typeof entry === "object" && entry !== null && "range" in entry ? entry.range : undefined);
  const mint = (itemId: string, index: number, raw: unknown) => {
    const source: AffordanceSource = { kind: "granted", itemId, index };
    const key = affordanceKey(source);
    ids.push(key);
    sources.push(source);
    const range = parseRange(raw as RawWeapon["range"]);
    if (range.length > 0) ranges[key] = range;
  };
  for (const id of pilot.gear) {
    const item = compendium.getById("pilot_gear", id) as RawActionBearer | undefined;
    if (!item) continue;
    const name = rawItemName(item);
    if (name !== undefined) names[id] = name;
    const actions: unknown[] = Array.isArray(item.actions) ? item.actions : [];
    actions.forEach((action, i) => mint(id, i, rawRange(action)));
    if (Array.isArray(item.deployables)) {
      const deployables: unknown[] = item.deployables;
      deployables.forEach((deployable, i) => mint(id, actions.length + i, rawRange(deployable)));
    }
  }
  return { ids, sources, names, ranges };
}

/**
 * Freeze the pilot's **gear-granted Limited pools** — the pilot-gear counterpart to
 * `freezeActionCaps`' item-level arm, which only ever walks a mech loadout (never `pilot.gear`). A
 * pilot-gear item's `Limited {N}` tag lives on the ITEM itself (`RawPilotGear.tags`), one level up
 * from its granted action(s) — RAW's "Expend a charge for the following effect" reads as a single
 * charge pool for the whole item (Patch/Frag Grenades/Stims/Corrective each author exactly one
 * granted action off that pool). It freezes as each record's `limited: {max}` (`decisions/0202`),
 * the same fact a mech system's Limited freezes, so `checkGrantedActionLegality` and the
 * `spend_action_use` fold read it unchanged — no kind-specific branch. Pilot gear prints no
 * `frequency`, so it freezes no rate.
 *
 * Mirrors {@link freezePilotGearActionIds}'s walk and composite-id namespace (`${gearId}:${i}`),
 * including every `deployables[]`-offset id — `pg_thermite_charge` is the worked case: its
 * `tg_limited val 1` freezes `{max:1}` onto `pg_thermite_charge:0` (its sole `deployables[0]` id;
 * it has zero `actions[]`). **No `pool:`** — no shipped pilot-gear item mints more than one
 * affordance, so each pool counts on its own key. If a future item authors both `actions[]` and
 * `deployables[]`, this needs the `pool: itemId` `freezeActionCaps` stamps. Its spent count is not
 * persisted: the commit extractor reads only a mech's `base.affordances`. An item with no
 * `Limited` tag, or an unresolved id, contributes nothing (honest-degrade).
 */
function freezePilotGearActionCaps(
  pilot: PilotData,
  compendium: Compendium,
): Record<string, AffordanceLimited> {
  const caps: Record<string, AffordanceLimited> = {};
  for (const id of pilot.gear) {
    const item = compendium.getById("pilot_gear", id) as (RawPilotGear & RawActionBearer) | undefined;
    if (!item) continue;
    const tag = item.tags?.find((t) => t.id === "tg_limited");
    const max = tag && typeof tag.val === "number" ? tag.val : undefined;
    if (!max || max <= 0) continue;
    const actionCount = Array.isArray(item.actions) ? item.actions.length : 0;
    for (let i = 0; i < actionCount; i++) caps[`${id}:${i}`] = { max };
    if (Array.isArray(item.deployables)) {
      for (let i = 0; i < item.deployables.length; i++) caps[`${id}:${actionCount + i}`] = { max };
    }
  }
  return caps;
}

/**
 * Freeze a mech's **on-foot pilot block** onto `spawnMech`'s frozen base — the
 * `ComputedPilotStats` 10-field seam (`computedPilot.combat`, the exact block {@link spawnPilot}
 * reads for a direct on-foot deploy) + callsign/name + the pilot's gear-weapon `WeaponProfile`s
 * (reusing {@link freezePilotGearWeapons} verbatim — the SAME `pilotgear:${i}` namespace a direct
 * deploy freezes) + the pilot's gear-EFFECT ids, so a Dismount/Eject-minted on-foot pilot's gear
 * effects gather off the SAME frozen list a direct {@link spawnPilot} deploy carries — never
 * omitted just because the pilot boarded a mech first — PLUS the pilot's gear-granted composite
 * affordance records, reusing {@link freezePilotGearActionIds}/
 * {@link freezePilotGearActionCaps} verbatim (the SAME two helpers {@link spawnPilot} calls),
 * so a dismounted/ejected pilot's gear reaches the SAME pressable, correctly-capped bar row a
 * direct `spawnPilot` deploy gets. **Both halves are load-bearing**: freezing the effect ids
 * without the affordance records leaves a dismounted pilot's gear with no granted row at all.
 * Dismount/Eject/destruction mint the on-foot pilot combatant purely from this snapshot, never a
 * live roster/compendium read mid-encounter.
 */
export function freezePilotBlock(pilotData: PilotData, computedPilot: ComputedPilot, compendium: Compendium): FrozenPilotBlock {
  const s = computedPilot.combat;
  const block: FrozenPilotBlock = {
    callsign: pilotData.callsign || pilotData.name,
    name: pilotData.name,
    size: s.size,
    hp: s.hp,
    armor: s.armor,
    evasion: s.evasion,
    edef: s.edef,
    speed: s.speed,
    sensorRange: s.sensorRange,
    save: s.save,
    attackBonus: s.attackBonus,
    weapons: freezePilotGearWeapons(pilotData, compendium),
  };
  const pilotGearEffectIds = freezePilotGearEffectIds(pilotData);
  if (pilotGearEffectIds.length > 0) block.pilotGearEffectIds = pilotGearEffectIds;
  // The affordance table for the pilot's gear mint — the ONE frozen action-economy fact the block
  // carries since `affordance-table` Phase 3.1 (the parallel `actionIds`/`actionUsageCaps` maps
  // that used to sit beside it are deleted). `spawnDismountedPilot` copies it straight onto the minted combatant's
  // base, so a Dismount/Eject-minted on-foot pilot gets the same pressable, correctly-capped bar
  // row a direct `spawnPilot` deploy does.
  const { sources: gearSources, names: gearNames, ranges: gearRanges } = freezePilotGearActionIds(pilotData, compendium);
  const gearAffordances = buildAffordances({ sources: gearSources, limited: freezePilotGearActionCaps(pilotData, compendium), names: gearNames });
  if (gearAffordances.length > 0) block.affordances = gearAffordances;
  if (Object.keys(gearRanges).length > 0) block.actionRanges = gearRanges;
  return block;
}

/**
 * Args for {@link spawnPilot}. Unlike {@link spawnMech}, there is no mech/frame to
 * resolve — the pilot's own `ComputedPilot.combat` block (Core p.73-76's on-foot
 * profile) IS the base, computed fresh at spawn. This is the plain direct GM drop with no
 * mech binding; the Dismount/Eject/destruction bridge instead freezes a `pilotBlock` onto the
 * *mech* at ITS spawn and mints the on-foot combatant from that frozen snapshot, never
 * re-reading the roster mid-fight.
 */
export type SpawnPilotArgs = {
  combatantId: string;
  pilotData: PilotData;
  compendium: Compendium;
};

/**
 * Spawn a **direct on-foot pilot combatant** — no mech, no roster mech index. Always
 * succeeds: `compute`'s `ComputedPilot.combat` block has no frame-resolution dependency (unlike
 * `spawnMech`), so there's no "half-built" pilot to reject.
 *
 * The on-foot restriction profile is baked into the frozen base:
 *   - **`maxHeat` omitted** — Biological (Core p.282's tech-immunity signal);
 *     `resolveHeatCascade` reads this absence to route incoming heat to Energy
 *     damage. NEVER give a pilot a real heat cap.
 *   - **`maxStructure`/`maxStress` omitted** — no consequence tracks on foot
 *     (presence-based, never a `null` sentinel); `resolveDamageCascade`'s no-structure-track
 *     branch (`hasStructureTrack`) routes the 0-HP case to "goes down, not destroyed" for a
 *     `pilot` (vs. an `object`/`deployable`).
 *   - **`saveTarget` = `10 + grit`** straight off `ComputedPilotStats`, and
 *     **`attackBonus`/`saveBonus` = grit** — the two are NOT the same number. RAW `:3970`
 *     gives an unmounted character GRIT on the save *roll*; `:1561-1562` gives GRIT to the
 *     save *target*. Conflating them is the defect `decisions/0163` records.
 *   - **`hull`/`agility`/`systems` = grit** — GRIT stands in for the contest axes too
 *     (Search-vs-pilot rolls defender agility, Grapple-break rolls hull).
 *   - **No `pilotEffectIds`** — RAW: no talents fire on foot (the *passive* stat
 *     bonuses from gear/talents/core-bonuses already fold into `combat` via
 *     `compute`; there is no active/triggered on-foot consumer to freeze for).
 *     Counterexample class: a talent that fires AT dismount (`t_black_thumb:1`) rides the mech's
 *     triggers (`resolve/triggers.ts`), not the on-foot pilot's frozen base.
 *   - **`pilotGearEffectIds` DOES freeze** — unlike talents, RAW pilot gear (Frag Grenades, a
 *     hardsuit's Boost-gated Flying, …) is something the pilot carries and uses on foot same as
 *     mounted, so this is the opposite scoping call from the bullet above, not an inconsistency.
 */
export function spawnPilot(args: SpawnPilotArgs): SpawnResult {
  const { combatantId, pilotData, compendium } = args;
  const { pilot: computedPilot } = compute(pilotData, compendium);
  const s = computedPilot.combat;

  const base: CombatantBase = {
    maxHp: s.hp,
    // No heat track — a pilot on foot: presence-based, `maxHeat` simply omitted, never a
    // `null` sentinel.
    armor: s.armor,
    evasion: s.evasion,
    edef: s.edef,
    speed: s.speed,
    size: s.size,
    // The on-foot carve-out (`:3970`): "Unmounted characters make all attacks and saves by
    // adding GRIT to the roll instead of using their triggers." That is a genuine axis-less
    // FLAT bonus, so it lives on `saveBonus` (read first by `saveBonusFor`) rather than on the
    // three grit-standing-in axes below — a pilot on foot does NOT roll HASE.
    saveBonus: computedPilot.grit,
    // The DC this pilot imposes when it forces a save — `10 + grit`, unchanged. Distinct from
    // `saveBonus` above: the target it imposes vs the bonus it adds to its own rolls.
    saveTarget: s.save,
    attackBonus: s.attackBonus,
    sensors: s.sensorRange,
    agility: computedPilot.grit,
    systems: computedPilot.grit,
    hull: computedPilot.grit,
    // No structure/stress track — mirrors `maxHeat` just above: presence-based,
    // `maxStructure`/`maxStress` simply omitted, never a `null` sentinel.
    // A pilot on foot at 0 HP goes Down and Out, not destroyed outright (`tables.ts`'s
    // `resolveDamageCascade` no-structure-track branch).
    destructionOutcome: "downAndOut",
    // A pilot is always PC-side, regardless of `controllerSlotId` (mirrors `spawnMech` — only
    // `drone` derives from it).
    allegiance: "pc",
  };

  const weapons = freezePilotGearWeapons(pilotData, compendium);
  if (weapons.length > 0) base.weapons = weapons;

  // A Sidearm-tagged gear weapon bumps the Fight once-per-turn allowance to 2.
  const allowances = sidearmFightAllowance(weapons);
  if (allowances) base.actionAllowances = allowances;

  // The pilot's gear-EFFECT ids (Frag Grenades'/Patch's/Corrective's DSL clauses, Mobility
  // Hardsuit's Boost-gated Flying), frozen onto the pilot itself — the correct carrier for
  // them; see `freezePilotGearEffectIds`.
  const pilotGearEffectIds = freezePilotGearEffectIds(pilotData);
  if (pilotGearEffectIds.length > 0) base.pilotGearEffectIds = pilotGearEffectIds;

  // The pilot's gear-granted affordance records: one per `actions[]`/`deployables[]` entry on
  // each carried gear item, each carrying its own Limited charge cap inline. The cap join is
  // spawn-local — `freezePilotGearActionCaps` is the pilot-gear counterpart to `freezeActionCaps`
  // (which walks a mech loadout only), and its output feeds `buildAffordances` rather than a
  // second frozen map on `base` (`decisions/0176`; `affordance-table` Phase 3.1).
  const { sources: gearSources, names: gearNames, ranges: gearRanges } = freezePilotGearActionIds(pilotData, compendium);
  const gearAffordances = buildAffordances({ sources: gearSources, limited: freezePilotGearActionCaps(pilotData, compendium), names: gearNames });
  if (gearAffordances.length > 0) base.affordances = gearAffordances;
  // Each gear action's RAW reach (Frag Grenades' Range 5), for the activate site and unit walls.
  if (Object.keys(gearRanges).length > 0) base.actionRanges = gearRanges;

  // The on-foot pilot's CORE vocabulary is `PILOT_ACTIONS` (RAW p.73-76 — Fight not Skirmish, no
  // systems), stamped as `pilot:*` composites.
  base.coreActionIds = coreActionIdsFor("pilot");

  return ok({
    id: combatantId,
    kind: "pilot",
    name: pilotData.callsign || pilotData.name,
    base,
    // No `heat`/`structure`/`stress` — mirrors `base.maxHeat`/`maxStructure`/`maxStress`'s
    // omission: absence, never a `0` placeholder. HP seeds from the roster's carried
    // `currentHp`, clamped to the max (absent ⇒ full).
    current: { hp: Math.max(0, Math.min(pilotData.currentHp ?? base.maxHp, base.maxHp)) },
    // BIOLOGICAL_STATUS — tech-immune except Lock On/Scan. `until_removed` mirrors every other
    // spawn-attached status.
    statuses: [{ name: BIOLOGICAL_STATUS, expiry: { kind: "until_removed" } as const }],
    sourceRef: pilotData.id,
    pos: null, // unplaced until a `move_token`/`place_token` deploys it onto the map.
    // Controlled by the slot that owns the source pilot, captured once at spawn (mirrors
    // `spawnMech`).
    controllerSlotId: pilotData.owner,
  });
}
